import { Button, FormControl, FormHelperText, Grid, MenuItem, MenuProps, Select, SelectChangeEvent, TextField } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { MainContainer } from "../../components/MainContainerComp";
import { decodeString } from '../../utils';
import { IErrorMessage, IErrorMessageList, ISecurityQuestionData, Question, questions } from "./SecurityQuestionData";
import { asteriskStyle, descriptionStyle, gridItemStyle } from "./styled";

interface ISecurityQuestionProps {
  data: ISecurityQuestionData,
  onSubmit: (data: ISecurityQuestionData) => void;
}

const getAllErrorMsgs = (
  question: Question | '',
  answer: string,
  isFormTouched: boolean
): {
  questionErrors: IErrorMessageList;
  answerErrors: IErrorMessageList;
} => {
  return {
    questionErrors: {
      noQuestion: {
        condition: !question && isFormTouched,
        message: "Must select a Question"
      }
    },
    answerErrors: {
      noAnswer: {
        condition: !answer && isFormTouched,
        message: "Must enter an Answer"
      },
      maxLength: {
        condition: answer.length > 50,
        message: "Answer cannot be longer than 50 characters"
      },
      /* ------------------------------ REFERENCE USE ----------------------------- */
      // invalidFormat: {
      //   condition: !dayjs(answer, 'DD.MM.YYYY', true).isValid(),
      //   message: "Answer must be in the format dd.mm.yyyy",
      //   appliesTo: [Question.AnniversaryDate]
      // }
    }
  }
};

const checkError = (error: IErrorMessage, question: Question | '') => {
  if (error.appliesTo)
    return error.condition && (error.appliesTo.some(x => question === x) || false);
  return error.condition;
}

const getErrorMsg = (errorMsgList: IErrorMessageList, question: Question | '') => {
  return Object.keys(errorMsgList).map(x => {
    const error = errorMsgList[x];
    return checkError(error, question) && error.message;
  }).reduce((acc, val) => acc || val, false) || '';
}

const hasAnyErrors = (errorMsgList: IErrorMessageList, question: Question | '') => {
  return Object.keys(errorMsgList)
    .map(x => checkError(errorMsgList[x], question))
    .some(x => x);
}

export const SecurityQuestion: React.FC<ISecurityQuestionProps> = ({
  data,
  onSubmit,
}) => {
  const { question, answer } = data;
  const decodedQuestion = decodeString(question);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | ''>(decodedQuestion as Question ?? '');
  const [userAnswer, setUserAnswer] = useState(answer ?? '');
  const [isFormTouched, setIsFormTouched] = useState(false);
  const errorMessages = getAllErrorMsgs(selectedQuestion, userAnswer, isFormTouched);
  const answerError = hasAnyErrors(errorMessages.answerErrors, selectedQuestion);
  const questionError = hasAnyErrors(errorMessages.questionErrors, selectedQuestion);
  const answerErrorMsg = getErrorMsg(errorMessages.answerErrors, selectedQuestion);
  const questionErrorMsg = getErrorMsg(errorMessages.questionErrors, selectedQuestion);
  const sameQuestion = (decodedQuestion === selectedQuestion && answer === userAnswer);
  const disabledSubmit = sameQuestion || answerError || questionError || !userAnswer.trim();

  const handleSubmit = () => {
    if (!!userAnswer.trim()) {
      onSubmit({
        question: selectedQuestion,
        answer: userAnswer.trimEnd(),
      });
    }
  };

  const handleSelectQuestion = (e: SelectChangeEvent<Question>) => {
    setSelectedQuestion(e.target.value as Question);
    setUserAnswer(e.target.value === decodedQuestion ? answer : '');
    setIsFormTouched(true);
  }

  const handleSelectAnswer = ({ target: { value } }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setUserAnswer(!!value.trim() ? value : '');
    setIsFormTouched(true);
  }

  return (
    <MainContainer>
      <Typography data-automation="description-text" sx={descriptionStyle}>
        Answer to the secret question will allow us to identify you should you lose the password and logon information.
      </Typography>
      <Grid container sx={{ justifyContent: 'center' }} spacing={2}>
        <Grid item md={6} xs={12}>
          <Grid item sx={gridItemStyle} xs={12}>
            <Typography data-automation="question-header">Question<Typography component={"span"} sx={asteriskStyle}>&nbsp;*</Typography></Typography>
            <FormControl fullWidth error={questionError}>
              <Select
                value={selectedQuestion}
                onChange={handleSelectQuestion}
                displayEmpty
                fullWidth
                inputProps={{
                  'data-automation': 'question-input',
                }}
                MenuProps={{
                  'data-automation': 'question-dropdown'
                } as unknown as MenuProps}
              >
                <MenuItem value="" disabled>
                  Select a question
                </MenuItem>
                {questions.map((question) => (
                  <MenuItem
                    data-automation="dropdown-option-#"
                    key={question}
                    value={question}
                  >
                    {decodeString(question)}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText data-automation="question-error-msg">{questionErrorMsg}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item sx={gridItemStyle} xs={12}>
            <Typography data-automation="answer-header">Answer<Typography component={"span"} sx={asteriskStyle}>&nbsp;*</Typography></Typography>
            <TextField
              fullWidth
              value={userAnswer}
              onChange={handleSelectAnswer}
              placeholder="Enter your answer"
              error={answerError}
              helperText={
                <FormHelperText data-automation="answer-error-msg">
                  {answerErrorMsg}
                </FormHelperText>
              }
              inputProps={{
                'data-automation': 'answer-input'
              }}
            />
          </Grid>
          <Grid sx={{ display: 'flex', justifyContent: 'center' }} item xs={12}>
            <Button
              disabled={disabledSubmit}
              size="large"
              variant="contained"
              onClick={handleSubmit}
              data-automation="submit-btn"
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </MainContainer>
  )
}